import type { ChartData, ChartFields, KpiTile } from '../types';
import { dateValue, humanise, isNumeric, looksTemporal } from './format';
import { buildFrame, isEmpty, sum } from './frame';
import { DEFAULT_TONE, TONES, inferIcon, type IconName, type Tone } from './icons';
import { formatValue, inferFormat, isStarFormat } from './valueFormat';

/**
 * Turning tile configuration into the handful of strings a tile draws.
 *
 * This is deliberately a pure module with no React in it. The hand-written KPI
 * fragment (`seed.ts`, `KPI_HTML`) mixed its column inference, its number
 * formatting and its markup into one inline `<script>`, which is exactly why
 * nothing about it could be changed or tested. Keeping the decisions here means
 * the rules are unit-testable and the component is only layout.
 *
 * Two entry points:
 *   `resolveTiles` — the author configured tiles; read each from its own frame.
 *   `inferTiles`   — nobody configured anything; draw something sensible anyway,
 *                    so dropping the form on a chart is not a blank card.
 */

export interface ResolvedTile {
  /** Stable React key. */
  id: string;
  label: string;
  caption?: string;
  description?: string;
  icon: IconName | null;
  tone: Tone;
  /** The figure as it should read. */
  display: string;
  /** Full-precision figure for the `title`, when `display` is rounded. */
  exactTitle?: string;
  /** Set when the tile draws stars rather than a figure. */
  rating: { value: number; max: number } | null;
  /** Fractional change against the comparison value; null when there is none. */
  change: number | null;
  /** True when the tile's own source returned nothing. */
  missing: boolean;
}

type Agg = NonNullable<KpiTile['agg']>;

const DEFAULT_AGG: Agg = 'latest';

const EMPTY_DATA: ChartData = { columns: [], rows: [] };

const present = (v: unknown): boolean => v !== null && v !== undefined && v !== '';

/**
 * Row order, oldest first, when the result carries a date column.
 *
 * `latest` and the change arrow used to be pure array positions, which made
 * them a bet on the API returning ascending rows. Any "most recent first"
 * endpoint inverts that bet: the OLDEST month is presented as the current
 * figure, and the delta is computed backwards, so a metric that grew 14% draws
 * a red downward arrow. A wrong magnitude is bad; a confidently wrong direction
 * on a headline tile is worse, because it reads as a finding rather than a
 * glitch.
 *
 * So sort by the date rather than trust the order. Returns null when there is
 * no date column or any value fails to parse — then the query's own order
 * stands, which is the only thing left to go on.
 */
const chronological = (data: ChartData): number[] | null => {
  for (const [index, _name] of data.columns.entries()) {
    const column = data.rows.map((row) => row[index]);
    if (!looksTemporal(column)) continue;

    const times = column.map(dateValue);
    // `looksTemporal` samples the first 20 rows, so a longer result can still
    // hold an unparseable value further down. Partial ordering would be worse
    // than none: it would move some rows and not others.
    if (times.some((t) => t === null)) return null;

    return data.rows
      .map((_, row) => row)
      .sort((a, b) => (times[a] as number) - (times[b] as number));
  }
  return null;
};

const columnValues = (data: ChartData, measure: string, order: number[] | null): unknown[] => {
  const index = data.columns.indexOf(measure);
  if (index < 0) return [];
  const rows = order ? order.map((at) => data.rows[at]) : data.rows;
  return rows.map((row) => row[index]).filter(present);
};

/**
 * The column a tile reads when the author named none.
 *
 * This is what lets a tile be just `{ query: 'downloads' }`. The catalogue's
 * column names are not something an author should have to look up — and for a
 * seeded base chart they are not even knowable without querying the live API,
 * so a seed that hard-coded them would be a guess. The first numeric column is
 * the same choice the frame layer makes for every other form.
 */
const firstNumericColumn = (data: ChartData): string | null => {
  for (const [i, name] of data.columns.entries()) {
    if (data.rows.slice(0, 20).some((row) => isNumeric(row[i]))) return name;
  }
  return null;
};

/**
 * Reduce a column to one value.
 *
 * Only the arithmetic aggregations need numbers; `latest` and `first` take the
 * value as it came, which is what keeps an upstream-formatted figure intact —
 * chart 65 returns Best Rank as the string "#1", and coercing that to a number
 * would turn a correct answer into NaN.
 */
const aggregate = (values: unknown[], agg: Agg): unknown => {
  if (!values.length) return null;

  if (agg === 'count') return new Set(values.map((v) => String(v))).size;
  if (agg === 'first') return values[0];
  if (agg === 'latest') return values[values.length - 1];

  const numbers = values.filter(isNumeric);
  // A non-numeric column cannot be summed or averaged. Falling back to the last
  // value is better than rendering NaN, and the author still sees a real figure.
  if (!numbers.length) return values[values.length - 1];

  switch (agg) {
    case 'sum':
      return numbers.reduce((a, b) => a + b, 0);
    case 'avg':
      return numbers.reduce((a, b) => a + b, 0) / numbers.length;
    case 'max':
      return Math.max(...numbers);
    case 'min':
      return Math.min(...numbers);
    default:
      return numbers[numbers.length - 1];
  }
};

const asTone = (value: string | undefined, fallback: Tone): Tone =>
  value && (TONES as readonly string[]).includes(value) ? (value as Tone) : fallback;

/**
 * An author's stored icon name, narrowed.
 *
 * `undefined` means "infer one", and an explicit empty string or 'none' means
 * "no icon" — a real choice an author should be able to make. A name that is no
 * longer in the set falls back to inference rather than drawing nothing, since
 * a renamed icon should not silently strip the tile.
 */
const asIcon = (value: string | undefined): IconName | null | undefined => {
  if (value === undefined) return undefined;
  if (value === '' || value === 'none') return null;
  return value in DEFAULT_TONE ? (value as IconName) : undefined;
};

/** One configured tile, read from the frame it names. */
export const resolveTile = (
  tile: KpiTile,
  frames: Record<string, ChartData>,
  fallback?: ChartData,
  index = 0,
): ResolvedTile => {
  // `Object.hasOwn`, not `frames[key]`: a stored tile naming `constructor` or
  // `toString` would otherwise resolve to an inherited Object.prototype member,
  // which is truthy, so the `??` fallback would not fire and the next line
  // would read `.columns` off a function. One bad tile key would take the whole
  // dashboard down for every viewer.
  const named = tile.query && Object.hasOwn(frames, tile.query) ? frames[tile.query] : undefined;
  const data = (tile.query ? named : fallback) ?? EMPTY_DATA;
  const agg = tile.agg ?? DEFAULT_AGG;
  const order = chronological(data);

  // Three cases, in order:
  //   `count` with no column → how many rows came back. The only honest source
  //      for a figure like "Countries", where chart 2 returns one row per
  //      country and nothing holds the total.
  //   a named column          → read it.
  //   neither                 → the first numeric column (see above).
  const countingRows = !tile.measure && agg === 'count';
  const measure = tile.measure ?? (countingRows ? null : firstNumericColumn(data));

  const label = tile.label?.trim() || (measure ? humanise(measure) : 'Rows');

  const values = measure ? columnValues(data, measure, order) : [];
  const raw = countingRows ? data.rows.length || null : measure ? aggregate(values, agg) : null;

  // Not `asIcon(...) ?? inferIcon(...)`: `null` is the author's "no icon" and is
  // nullish, so `??` fell straight through to inference and the choice was
  // silently ignored. Only `undefined` — "they never said" — infers.
  const chosen = asIcon(tile.icon);
  const icon =
    chosen === undefined ? inferIcon(tile.label, measure ?? undefined, tile.caption) : chosen;
  const format = tile.format ?? inferFormat(tile.label, measure ?? undefined);

  // The comparison: an explicit column, else the row before the latest.
  let previous: number | null = null;
  if (tile.compareTo) {
    const compared = aggregate(columnValues(data, tile.compareTo, order), agg);
    previous = isNumeric(compared) ? compared : null;
  } else if (agg === 'latest' && measure) {
    const numbers = values.filter(isNumeric);
    previous = numbers.length > 1 ? numbers[numbers.length - 2] : null;
  }

  const current = isNumeric(raw) ? raw : null;
  const change =
    current !== null && previous !== null && previous !== 0
      ? (current - previous) / Math.abs(previous)
      : null;

  const ratingMax = isStarFormat(format) ? (format.max ?? 5) : 0;

  return {
    id: `${tile.query ?? '_'}:${tile.measure ?? measure ?? '_rows'}:${index}`,
    label,
    caption: tile.caption?.trim() || undefined,
    description: tile.description?.trim() || undefined,
    icon,
    tone: asTone(tile.tone, icon ? DEFAULT_TONE[icon] : 'blue'),
    display: formatValue(raw, format),
    exactTitle:
      current !== null ? current.toLocaleString('en-GB', { maximumFractionDigits: 4 }) : undefined,
    rating: ratingMax && current !== null ? { value: current, max: ratingMax } : null,
    change,
    missing: !present(raw),
  };
};

export const resolveTiles = (
  tiles: KpiTile[],
  frames: Record<string, ChartData>,
  fallback?: ChartData,
): ResolvedTile[] => tiles.map((tile, i) => resolveTile(tile, frames, fallback, i));

/** How many inferred tiles are worth drawing before the row stops being a row. */
const MAX_INFERRED = 8;

/**
 * Tiles for a chart whose author has configured none.
 *
 * Two real shapes come back from the catalogue and both are a KPI row already:
 * a single summary row of several measures (chart 47 "App Overview"), and a
 * monthly series whose latest point is the headline (charts 8, 9). The frame
 * layer already tells the two apart, so this reads the frame rather than
 * re-inferring from the columns.
 */
export const inferTiles = (data: ChartData, fields?: ChartFields): ResolvedTile[] => {
  const frame = buildFrame(data, fields);
  if (isEmpty(frame)) return [];

  // A single summary row: the frame puts each measure in `categories` and its
  // value in the one series — one tile per measure.
  if (!frame.temporal && frame.series.length === 1 && frame.categoryName === '') {
    return frame.categories
      .slice(0, MAX_INFERRED)
      .map((name, i) => build(name, frame.series[0].values[i] ?? null, null, i));
  }

  // Otherwise one tile per series: the latest period, compared with the one
  // before it when the categories are periods.
  return frame.series.slice(0, MAX_INFERRED).map((series, i) => {
    const numbers = series.values.filter(isNumeric);
    const value = frame.temporal ? (numbers[numbers.length - 1] ?? null) : sum(series.values);
    const previous = frame.temporal && numbers.length > 1 ? numbers[numbers.length - 2] : null;
    return build(series.name, value, previous, i);
  });
};

const build = (
  name: string,
  value: number | null,
  previous: number | null,
  index: number,
): ResolvedTile => {
  const label = humanise(name);
  const icon = inferIcon(label, name);
  const format = inferFormat(label, name);
  const ratingMax = isStarFormat(format) ? (format.max ?? 5) : 0;

  return {
    id: `inferred:${name}:${index}`,
    label,
    icon,
    tone: icon ? DEFAULT_TONE[icon] : 'blue',
    display: formatValue(value, format),
    exactTitle:
      value !== null ? value.toLocaleString('en-GB', { maximumFractionDigits: 4 }) : undefined,
    rating: ratingMax && value !== null ? { value, max: ratingMax } : null,
    change:
      value !== null && previous !== null && previous !== 0
        ? (value - previous) / Math.abs(previous)
        : null,
    missing: value === null,
  };
};
